import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { clearToken, getToken, setToken } from './api';

const AuthContext = createContext(null);

// localStorage keys
const LS_MEMBERSHIPS = 'priviax_memberships';
const LS_ACTIVE_ORG = 'priviax_active_org_id';
const LS_LAST_SELECTION = 'priviax_last_org_selection'; // yyyy-mm-dd

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [memberships, setMemberships] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_MEMBERSHIPS) || '[]'); }
    catch { return []; }
  });
  const [loading, setLoading] = useState(true);

  const persistMemberships = useCallback((list) => {
    setMemberships(list || []);
    try { localStorage.setItem(LS_MEMBERSHIPS, JSON.stringify(list || [])); }
    catch (_e) { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null); setOrganization(null); persistMemberships([]); setLoading(false);
      return;
    }
    try {
      const [{ data: me }, { data: mem }] = await Promise.all([
        api.get('/auth/me'),
        api.get('/auth/memberships').catch(() => ({ data: { memberships: [] } })),
      ]);
      setUser(me.user);
      setOrganization(me.organization);
      persistMemberships(mem.memberships || []);
    } catch (_e) {
      setUser(null); setOrganization(null); persistMemberships([]);
    } finally {
      setLoading(false);
    }
  }, [persistMemberships]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    setOrganization(data.organization);
    persistMemberships(data.memberships || []);
    try { localStorage.setItem(LS_ACTIVE_ORG, data.organization?.id); } catch (_e) { /* ignore */ }
    return data;
  }, [persistMemberships]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    setToken(data.token);
    setUser(data.user);
    setOrganization(data.organization);
    persistMemberships([{
      organization_id: data.organization.id,
      role: data.user.role,
      razon_social: data.organization.razon_social,
      plan: data.organization.plan,
    }]);
    try { localStorage.setItem(LS_ACTIVE_ORG, data.organization.id); } catch (_e) { /* ignore */ }
    return data;
  }, [persistMemberships]);

  const selectOrg = useCallback(async (organizationId) => {
    const { data } = await api.post('/auth/select-org', { organization_id: organizationId });
    setToken(data.token);
    setUser(data.user);
    setOrganization(data.organization);
    try {
      localStorage.setItem(LS_ACTIVE_ORG, organizationId);
      localStorage.setItem(LS_LAST_SELECTION, todayKey());
    } catch (_e) { /* ignore */ }
    return data;
  }, []);

  const markSelectionShown = useCallback(() => {
    try { localStorage.setItem(LS_LAST_SELECTION, todayKey()); } catch (_e) { /* ignore */ }
  }, []);

  const shouldShowOrgPicker = useCallback(() => {
    if (!memberships || memberships.length < 2) return false;
    try {
      const last = localStorage.getItem(LS_LAST_SELECTION);
      return last !== todayKey();
    } catch (_e) { return true; }
  }, [memberships]);

  const logout = useCallback(() => {
    clearToken();
    try {
      localStorage.removeItem(LS_MEMBERSHIPS);
      localStorage.removeItem(LS_ACTIVE_ORG);
      localStorage.removeItem(LS_LAST_SELECTION);
    } catch (_e) { /* ignore */ }
    setUser(null); setOrganization(null); setMemberships([]);
    window.location.href = '/login';
  }, []);

  const updateOrganization = useCallback((org) => setOrganization(org), []);

  const value = useMemo(
    () => ({
      user, organization, memberships, loading,
      login, register, logout, refresh, updateOrganization,
      selectOrg, shouldShowOrgPicker, markSelectionShown,
    }),
    [user, organization, memberships, loading, login, register, logout, refresh,
     updateOrganization, selectOrg, shouldShowOrgPicker, markSelectionShown],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
